import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity, ArrowLeft, BedDouble, Building2, CalendarDays, Database, ExternalLink, Globe, Power, PowerOff, Radio, RefreshCw, ShieldAlert, Tag, Trash2, Upload, Wand2,
} from 'lucide-react';
import {
  clearDemoData, getChannelCatalog, getDemoData, getHotel, listConnections, listHotels, listRatePlans, listRoomCategories, listRooms, listSyncJobs, provisionChannex, requestSync, seedDemoData, setHotelActive, testConnection,
} from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import type { ChannelConnection, ChannexProvisionWarning, RatePlan, Room, RoomCategory, SyncJob } from '@/lib/api/types';
import { channexWarningText, provisionSummary } from '@/features/channex/provision';
import { formatDate, formatDateTime, formatMoney, formatNumber, formatRelative, plural } from '@/lib/format';
import { useDebounced, useUrlParam } from '@/lib/hooks';
import { AccessOrError } from '@/components/ui/access';
import { Badge, IdChip, type Tone } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, Prop, Stat } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Notice, QueryError, Skeleton } from '@/components/ui/feedback';
import { FilterSelect } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/page';
import { Tabs } from '@/components/ui/tabs';
import { TableToolbar } from '@/components/ui/toolbar';
import { toast } from '@/components/ui/toast';
import { useOrganizations } from '@/features/organizations/use-organizations';

type Tab = 'overview' | 'rooms' | 'channels' | 'rates' | 'demo';

const ROOM_STATUS: Record<string, { label: string; tone: Tone }> = {
  vacant: { label: 'Свободен', tone: 'ok' }, occupied: { label: 'Занят', tone: 'accent' }, blocked: { label: 'Заблокирован', tone: 'warn' }, out_of_service: { label: 'Не обслуживается', tone: 'danger' },
};
const HK_STATUS: Record<string, { label: string; tone: Tone }> = {
  clean: { label: 'Чистый', tone: 'ok' }, inspected: { label: 'Проверен', tone: 'ok' }, dirty: { label: 'Грязный', tone: 'warn' }, repair: { label: 'Ремонт', tone: 'danger' },
};
const CONN_STATUS: Record<string, { label: string; tone: Tone }> = {
  connected: { label: 'Подключён', tone: 'ok' }, syncing: { label: 'Синхронизация', tone: 'accent' }, error: { label: 'Ошибка', tone: 'danger' }, disconnected: { label: 'Отключён', tone: 'neutral' }, paused: { label: 'Пауза', tone: 'warn' },
};
const JOB_STATUS: Record<string, { label: string; tone: Tone }> = {
  queued: { label: 'В очереди', tone: 'neutral' }, running: { label: 'Выполняется', tone: 'accent' }, success: { label: 'Успех', tone: 'ok' }, error: { label: 'Ошибка', tone: 'danger' },
};
const RATE_TYPE: Record<string, string> = { base: 'Базовый', seasonal: 'Сезонный', weekend: 'Выходные', promo: 'Промо', long_stay: 'Длительное' };
const BED: Record<string, string> = { single: 'односпальная', double: 'двуспальная', twin: 'две односпальные', king: 'king', queen: 'queen', suite: 'люкс' };

/**
 * One hotel, as far as the backend lets a platform admin see it.
 *
 * The header and the active switch come from the cross-tenant `/admin/hotels`
 * list. Everything under the tabs is an "any member" route: tried, and when
 * the backend answers 403, said so rather than shown broken. The two levers
 * that exist — a feed probe and a queued sync — are here because the list's
 * "sync error" column is otherwise a fact one can only look at.
 */
export function HotelPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const [tab, setTab] = useUrlParam('tab', 'overview');
  const hotels = useQuery({ queryKey: ['admin', 'hotels'], queryFn: listHotels });
  const organizations = useOrganizations();
  const hotel = hotels.data?.find((h) => h.id === id) ?? null;

  const toggle = useMutation({
    mutationFn: (isActive: boolean) => setHotelActive(id, isActive),
    onSuccess: (_, v) => { toast('success', v ? 'Отель включён' : 'Отель выключен'); qc.invalidateQueries({ queryKey: ['admin', 'hotels'] }); qc.invalidateQueries({ queryKey: ['admin', 'stats'] }); },
    onError: (err) => toast('error', 'Не удалось изменить', describeError(err)),
  });

  if (hotels.isPending) return <><Skeleton className="mb-6 h-8 w-64" /><div className="flex gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[132px] flex-1" />)}</div></>;
  if (hotels.isError) return <QueryError error={hotels.error} onRetry={() => hotels.refetch()} />;
  if (!hotel) return <Notice tone="warn" title="Такого отеля нет в списке платформы"><Link to="/hotels" className="underline">К списку отелей</Link></Notice>;

  const t = (tab as Tab) in { overview: 1, rooms: 1, channels: 1, rates: 1, demo: 1 } ? (tab as Tab) : 'overview';

  return (
    <>
      <Link to="/hotels" className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-muted hover:text-accent-ink"><ArrowLeft size={13} />Все отели</Link>
      <PageHeader
        title={hotel.name}
        subtitle={(
          <span className="flex flex-wrap items-center gap-2">
            <span>{[hotel.city, hotel.country].filter(Boolean).join(', ')}</span>
            <Badge tone={hotel.isActive ? 'ok' : 'neutral'} dot>{hotel.isActive ? 'Активен' : 'Выключен'}</Badge>
            <Link to={`/organizations/${hotel.organizationId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-accent-ink hover:underline"><ExternalLink size={12} />{organizations.label(hotel.organizationId)}</Link>
            <IdChip id={hotel.id} />
          </span>
        )}
        action={(
          <Button variant={hotel.isActive ? 'danger' : 'primary'} size="sm" loading={toggle.isPending} icon={hotel.isActive ? <PowerOff size={13} /> : <Power size={13} />} onClick={() => toggle.mutate(!hotel.isActive)}>
            {hotel.isActive ? 'Выключить отель' : 'Включить отель'}
          </Button>
        )}
      />

      {hotel.syncError && <Notice tone="danger" title="Ошибка синхронизации" className="mb-4">{hotel.syncError}</Notice>}

      <div className="mb-6 flex flex-wrap gap-3">
        <Stat label="Номера" value={formatNumber(hotel.totalRooms)} icon={<BedDouble size={18} />} tone="neutral" />
        <Stat label="Каналы" value={formatNumber(hotel.connectedChannels)} icon={<RefreshCw size={18} />} tone={hotel.connectedChannels ? 'ok' : 'neutral'} />
        <Stat label="Последний синк" value={hotel.lastSyncAt ? formatRelative(hotel.lastSyncAt) : '—'} hint={hotel.lastSyncAt ? formatDateTime(hotel.lastSyncAt) : 'синхронизаций не было'} icon={<Activity size={18} />} tone={hotel.syncError ? 'danger' : 'accent'} />
        <Stat label="Зарегистрирован" value={formatDate(hotel.createdAt)} icon={<CalendarDays size={18} />} tone="neutral" />
      </div>

      <Tabs
        className="mb-5"
        value={t}
        onChange={setTab}
        tabs={[
          { value: 'overview', label: 'Профиль', icon: <Building2 size={15} /> },
          { value: 'rooms', label: 'Номера', icon: <BedDouble size={15} /> },
          { value: 'channels', label: 'Каналы', icon: <Radio size={15} /> },
          { value: 'rates', label: 'Тарифы', icon: <Tag size={15} /> },
          { value: 'demo', label: 'Демо-данные', icon: <Database size={15} /> },
        ]}
      />

      {t === 'overview' && <ProfileTab hotelId={id} />}
      {t === 'rooms' && <RoomsTab hotelId={id} />}
      {t === 'channels' && <ChannelsTab hotelId={id} />}
      {t === 'rates' && <RatesTab hotelId={id} />}
      {t === 'demo' && <DemoTab hotelId={id} />}
    </>
  );
}

// ── Профиль ───────────────────────────────────────────────────────────────────

function ProfileTab({ hotelId }: { hotelId: string }) {
  const profile = useQuery({ queryKey: ['hotel', hotelId], queryFn: () => getHotel(hotelId), retry: false });
  if (profile.isPending) return <Skeleton className="h-64" />;
  if (profile.isError) return <AccessOrError error={profile.error} what="Реквизиты отеля" route="/hotels/{id}" onRetry={() => profile.refetch()} />;
  const h = profile.data;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader icon={<Building2 size={18} />} title="Реквизиты" />
        <dl className="divide-y divide-line">
          <Prop label="Название">{h.name}</Prop>
          <Prop label="Slug" mono>{h.slug}</Prop>
          <Prop label="Тип">{h.propertyType ?? '—'}</Prop>
          <Prop label="Звёзды">{h.starRating ? '★'.repeat(h.starRating) : '—'}</Prop>
          <Prop label="Адрес">{[h.address, h.city, h.country].filter(Boolean).join(', ')}</Prop>
          <Prop label="Телефон">{h.phone ?? '—'}</Prop>
          <Prop label="E-mail">{h.email ?? '—'}</Prop>
          <Prop label="Сайт">{h.website ? <a href={h.website} target="_blank" rel="noreferrer" className="text-accent-ink hover:underline">{h.website}</a> : '—'}</Prop>
          <Prop label="Юр. лицо">{h.legalName ?? '—'}</Prop>
        </dl>
      </Card>
      <Card>
        <CardHeader icon={<Globe size={18} />} title="Работа" />
        <dl className="divide-y divide-line">
          <Prop label="Часовой пояс · валюта · язык">{h.timezone} · {h.currency} · {h.language}</Prop>
          <Prop label="Заезд / выезд">{h.checkInTime} / {h.checkOutTime}</Prop>
          <Prop label="Модуль бронирования"><Badge tone={h.bookingEngineEnabled ? 'ok' : 'neutral'} dot>{h.bookingEngineEnabled ? 'включён' : 'выключен'}</Badge></Prop>
          <Prop label="Онлайн-оплата"><Badge tone={h.onlinePaymentEnabled ? 'ok' : 'neutral'} dot>{h.onlinePaymentEnabled ? 'включена' : 'выключена'}</Badge></Prop>
          <Prop label="Способы оплаты">{h.paymentMethods.length ? h.paymentMethods.join(', ') : '—'}</Prop>
          <Prop label="Удобства">{h.facilities.length ? `${h.facilities.length}: ${h.facilities.slice(0, 6).join(', ')}${h.facilities.length > 6 ? '…' : ''}` : '—'}</Prop>
          <Prop label="Языки персонала">{h.languagesSpoken.length ? h.languagesSpoken.join(', ') : '—'}</Prop>
          <Prop label="Фото">{formatNumber(h.photos.length)}</Prop>
          <Prop label="Обновлён">{formatDateTime(h.updatedAt)}</Prop>
        </dl>
      </Card>
      {h.description && <Card className="lg:col-span-2"><CardHeader title="Описание" /><p className="whitespace-pre-line text-sm leading-6 text-ink">{h.description}</p></Card>}
    </div>
  );
}

// ── Номера ────────────────────────────────────────────────────────────────────

function RoomsTab({ hotelId }: { hotelId: string }) {
  const categories = useQuery({ queryKey: ['hotel', hotelId, 'categories'], queryFn: () => listRoomCategories(hotelId), retry: false });
  const rooms = useQuery({ queryKey: ['hotel', hotelId, 'rooms'], queryFn: () => listRooms(hotelId), retry: false });
  const [q, setQ] = useUrlParam('rq');
  const [cat, setCat] = useUrlParam('cat', 'all');
  const [status, setStatus] = useUrlParam('rstatus', 'all');
  const query = useDebounced(q.trim().toLowerCase(), 150);
  const catName = useMemo(() => new Map((categories.data ?? []).map((c) => [c.id, c.name])), [categories.data]);
  const roomsByCat = useMemo(() => { const m = new Map<string, number>(); for (const r of rooms.data ?? []) m.set(r.categoryId, (m.get(r.categoryId) ?? 0) + 1); return m; }, [rooms.data]);

  const filtered = useMemo(() => {
    let rows = rooms.data ?? [];
    if (query) rows = rows.filter((r) => `${r.number} ${r.building ?? ''} ${r.floorName ?? ''} ${catName.get(r.categoryId) ?? ''} ${r.notes ?? ''}`.toLowerCase().includes(query));
    if (cat !== 'all') rows = rows.filter((r) => r.categoryId === cat);
    if (status !== 'all') rows = rows.filter((r) => r.status === status);
    return rows;
  }, [rooms.data, query, cat, status, catName]);

  if (rooms.isError) return <AccessOrError error={rooms.error} what="Номера" route="/hotels/{id}/rooms" onRetry={() => rooms.refetch()} />;

  const catColumns: Column<RoomCategory>[] = [
    { key: 'name', header: 'Категория', sortValue: (c) => c.name, render: (c) => <><div className="font-semibold text-ink">{c.name}</div>{c.description && <div className="line-clamp-1 text-xs text-muted">{c.description}</div>}</> },
    { key: 'rooms', header: 'Номеров', align: 'right', sortValue: (c) => roomsByCat.get(c.id) ?? 0, render: (c) => formatNumber(roomsByCat.get(c.id) ?? 0) },
    { key: 'basePrice', header: 'Базовая цена', align: 'right', sortValue: (c) => c.basePrice, render: (c) => formatNumber(c.basePrice) },
    { key: 'capacity', header: 'Мест', align: 'right', sortValue: (c) => c.capacity, render: (c) => <>{c.capacity}{c.maxAdults !== null && <span className="text-xs text-muted"> ({c.maxAdults}+{c.maxChildren ?? 0})</span>}</> },
    { key: 'bedType', header: 'Кровать', sortValue: (c) => c.bedType, hideBelow: 'md', render: (c) => BED[c.bedType] ?? c.bedType },
    { key: 'sizeSqm', header: 'м²', align: 'right', sortValue: (c) => c.sizeSqm, hideBelow: 'lg', render: (c) => c.sizeSqm ?? '—' },
    { key: 'isActive', header: 'Статус', align: 'center', sortValue: (c) => c.isActive, render: (c) => <Badge tone={c.isActive ? 'ok' : 'neutral'} dot>{c.isActive ? 'Активна' : 'Скрыта'}</Badge> },
  ];

  const roomColumns: Column<Room>[] = [
    { key: 'number', header: 'Номер', sortValue: (r) => r.number, render: (r) => <><span className="font-semibold text-ink">{r.number}</span>{r.notes && <div className="line-clamp-1 text-xs text-muted">{r.notes}</div>}</> },
    { key: 'category', header: 'Категория', sortValue: (r) => catName.get(r.categoryId) ?? '', render: (r) => catName.get(r.categoryId) ?? <IdChip id={r.categoryId} /> },
    { key: 'floor', header: 'Этаж / корпус', sortValue: (r) => r.floor, hideBelow: 'md', render: (r) => [r.floorName ?? (r.floor !== null ? `${r.floor} этаж` : null), r.building].filter(Boolean).join(' · ') || '—' },
    { key: 'status', header: 'Состояние', sortValue: (r) => r.status, render: (r) => { const s = ROOM_STATUS[r.status]; return <Badge tone={s?.tone ?? 'neutral'} dot>{s?.label ?? r.status}</Badge>; } },
    { key: 'hk', header: 'Уборка', sortValue: (r) => r.housekeepingStatus, hideBelow: 'md', render: (r) => { const s = HK_STATUS[r.housekeepingStatus]; return <Badge tone={s?.tone ?? 'neutral'}>{s?.label ?? r.housekeepingStatus}</Badge>; } },
    { key: 'priceOverride', header: 'Своя цена', align: 'right', sortValue: (r) => r.priceOverride, hideBelow: 'lg', render: (r) => r.priceOverride !== null ? formatNumber(r.priceOverride) : <span className="text-faint">—</span> },
    { key: 'isActive', header: 'Активен', align: 'center', sortValue: (r) => r.isActive, render: (r) => <Badge tone={r.isActive ? 'ok' : 'neutral'} dot>{r.isActive ? 'да' : 'нет'}</Badge> },
  ];

  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-3 text-sm font-bold text-ink">Категории номеров</h2>
        {categories.isError ? <AccessOrError error={categories.error} what="Категории" route="/hotels/{id}/room-categories" /> : (
          <DataTable id="c_" rows={categories.data ?? []} columns={catColumns} rowKey={(c) => c.id} defaultSort={{ key: 'name', dir: 'asc' }} loading={categories.isPending} compact empty={{ icon: <BedDouble size={28} />, title: 'Категорий нет' }} />
        )}
      </section>
      <section>
        <h2 className="mb-3 text-sm font-bold text-ink">Номерной фонд</h2>
        <TableToolbar search={{ value: q, onChange: setQ, placeholder: 'Номер, корпус, категория' }} shown={filtered.length} total={rooms.data?.length} onReset={q || cat !== 'all' || status !== 'all' ? () => { setQ(''); setCat('all'); setStatus('all'); } : null}>
          <FilterSelect ariaLabel="Категория" value={cat} onChange={setCat} options={[{ value: 'all', label: 'Все категории' }, ...(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))]} />
          <FilterSelect ariaLabel="Состояние" value={status} onChange={setStatus} options={[{ value: 'all', label: 'Любое состояние' }, ...Object.entries(ROOM_STATUS).map(([v, s]) => ({ value: v, label: s.label }))]} />
        </TableToolbar>
        <DataTable id="r_" rows={filtered} columns={roomColumns} rowKey={(r) => r.id} defaultSort={{ key: 'number', dir: 'asc' }} loading={rooms.isPending} empty={{ icon: <BedDouble size={28} />, title: rooms.data?.length ? 'Ничего не подошло под фильтры' : 'Номеров нет' }} />
      </section>
    </div>
  );
}

// ── Каналы ────────────────────────────────────────────────────────────────────

function ChannelsTab({ hotelId }: { hotelId: string }) {
  const qc = useQueryClient();
  const connections = useQuery({ queryKey: ['hotel', hotelId, 'connections'], queryFn: () => listConnections(hotelId), retry: false });
  const jobs = useQuery({ queryKey: ['hotel', hotelId, 'sync-jobs'], queryFn: () => listSyncJobs(hotelId), retry: false, refetchInterval: (q) => (q.state.data?.some((j) => j.status === 'queued' || j.status === 'running') ? 5000 : false) });

  const probe = useMutation({
    mutationFn: (c: ChannelConnection) => testConnection(hotelId, c.provider, c.id),
    onSuccess: (r, c) => toast(r.ok ? 'success' : 'error', r.ok ? `Фид ${c.provider} отвечает` : `Фид ${c.provider} не отвечает`, `${formatNumber(r.latencyMs)} мс`),
    onError: (err) => toast('error', 'Проверка не удалась', describeError(err)),
  });
  const sync = useMutation({
    mutationFn: (c: ChannelConnection) => requestSync(hotelId, c.provider, c.id),
    onSuccess: () => { toast('success', 'Синхронизация поставлена в очередь'); qc.invalidateQueries({ queryKey: ['hotel', hotelId, 'sync-jobs'] }); },
    onError: (err) => toast('error', 'Не удалось запустить', describeError(err)),
  });

  if (connections.isError) return <AccessOrError error={connections.error} what="Подключения каналов" route="/hotels/{id}/channels" onRetry={() => connections.refetch()} />;

  const jobColumns: Column<SyncJob>[] = [
    { key: 'createdAt', header: 'Когда', sortValue: (j) => j.createdAt, className: 'whitespace-nowrap text-xs', render: (j) => <span title={j.createdAt}>{formatDateTime(j.createdAt)}</span> },
    { key: 'provider', header: 'Провайдер', sortValue: (j) => j.provider, render: (j) => <span className="font-medium text-ink">{j.provider}</span> },
    { key: 'trigger', header: 'Причина', sortValue: (j) => j.trigger, hideBelow: 'md', render: (j) => <span className="text-xs text-muted">{j.trigger}</span> },
    { key: 'status', header: 'Статус', sortValue: (j) => j.status, render: (j) => { const s = JOB_STATUS[j.status]; return <Badge tone={s?.tone ?? 'neutral'} dot>{s?.label ?? j.status}</Badge>; } },
    { key: 'stats', header: 'Результат', hideBelow: 'md', render: (j) => j.error ? <span className="text-xs text-danger">{j.error}</span> : j.stats && Object.keys(j.stats).length ? (
      <span className="text-xs text-muted">{[['imported', 'импорт'], ['updated', 'обновл.'], ['cancelled', 'отмен.'], ['blocks', 'блоки'], ['conflicts', 'конфл.']].filter(([k]) => (j.stats as Record<string, number>)[k]).map(([k, l]) => `${l} ${(j.stats as Record<string, number>)[k]}`).join(' · ') || (j.stats.unchanged ? 'без изменений' : '—')}</span>
    ) : <span className="text-faint">—</span> },
    { key: 'duration', header: 'Длит.', align: 'right', sortValue: (j) => (j.startedAt && j.finishedAt ? new Date(j.finishedAt).getTime() - new Date(j.startedAt).getTime() : null), hideBelow: 'lg', render: (j) => j.startedAt && j.finishedAt ? `${formatNumber((new Date(j.finishedAt).getTime() - new Date(j.startedAt).getTime()) / 1000, 1)} с` : '—' },
  ];

  return (
    <div className="space-y-6">
      <ChannexPublishCard hotelId={hotelId} connection={connections.data?.find((c) => c.provider === 'channex') ?? null} loading={connections.isPending} />
      <section>
        <h2 className="mb-3 text-sm font-bold text-ink">Подключения</h2>
        {connections.isPending ? <Skeleton className="h-32" /> : connections.data.length === 0 ? (
          <Notice tone="neutral" title="Каналы не подключены">Отель не подключил ни channel manager, ни iCal-ленты.</Notice>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {connections.data.map((c) => {
              const s = CONN_STATUS[c.status];
              return (
                <Card key={c.id} className="flex flex-col gap-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent-ink"><Radio size={16} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2"><span className="font-semibold text-ink">{c.provider}</span>{c.channel && <Badge mono>{c.channel}</Badge>}<Badge tone={s?.tone ?? 'neutral'} dot>{s?.label ?? c.status}</Badge></div>
                      <p className="text-xs text-muted">{c.feedHost ?? c.externalPropertyId ?? c.id}</p>
                    </div>
                  </div>
                  {c.lastError && <Notice tone="danger">{c.lastError}</Notice>}
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <dt className="text-muted">Броней через канал</dt><dd className="text-right text-ink tabular-nums">{formatNumber(c.reservationsCount)}</dd>
                    <dt className="text-muted">Последний синк</dt><dd className="text-right text-ink">{c.lastSyncAt ? formatRelative(c.lastSyncAt) : '—'}</dd>
                    <dt className="text-muted">Следующий</dt><dd className="text-right text-ink">{c.nextSyncAt ? formatRelative(c.nextSyncAt) : '—'}</dd>
                    <dt className="text-muted">Интервал</dt><dd className="text-right text-ink">{c.syncIntervalMinutes} мин</dd>
                    {c.channels && <><dt className="text-muted">OTA-каналы</dt><dd className="text-right text-ink">{c.channels.filter((x) => x.isActive).length} активных из {c.channels.length}</dd></>}
                  </dl>
                  {c.channels && c.channels.length > 0 && (
                    <ul className="divide-y divide-line text-xs">
                      {c.channels.map((x) => (
                        <li key={x.id} className="flex items-center gap-2 py-1.5">
                          <span className="min-w-0 flex-1 truncate text-ink">{x.title ?? x.channel ?? x.id}</span>
                          <span className="text-muted">{plural(x.mappedRatePlans, ['тариф', 'тарифа', 'тарифов'])}</span>
                          {x.expectedRemovalDate && <Badge tone="warn">удаление {formatDate(x.expectedRemovalDate)}</Badge>}
                          <Badge tone={x.isActive ? 'ok' : 'neutral'} dot>{x.isActive ? 'вкл' : 'выкл'}</Badge>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="mt-auto flex justify-end gap-2 border-t border-line pt-3">
                    <Button size="sm" variant="outline" icon={<Activity size={13} />} loading={probe.isPending && probe.variables?.id === c.id} onClick={() => probe.mutate(c)}>Проверить</Button>
                    <Button size="sm" variant="soft" icon={<RefreshCw size={13} />} loading={sync.isPending && sync.variables?.id === c.id} onClick={() => sync.mutate(c)}>Синхронизировать</Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>
      <section>
        <h2 className="mb-3 text-sm font-bold text-ink">Журнал синхронизаций</h2>
        {jobs.isError ? <AccessOrError error={jobs.error} what="Задачи синхронизации" route="/hotels/{id}/sync-jobs" /> : (
          <DataTable id="j_" rows={jobs.data ?? []} columns={jobColumns} rowKey={(j) => j.id} defaultSort={{ key: 'createdAt', dir: 'desc' }} loading={jobs.isPending} empty={{ icon: <Activity size={28} />, title: 'Синхронизаций ещё не было' }} />
        )}
      </section>
    </div>
  );
}

/**
 * Publishing the hotel on Channex — moved here from the hotel cabinet.
 *
 * The first run creates a property on the shared, billable Channex account, so
 * it is the platform's decision, not the hotel's. After it the backend's
 * auto-provisioner carries new categories and channel plans by itself; the
 * button stays as the repair (idempotent: renames in place, deletes nothing).
 * The warnings are the point of showing the result: Channex accepts a property
 * without a postcode, Booking.com refuses it weeks later.
 */
function ChannexPublishCard({ hotelId, connection, loading }: { hotelId: string; connection: ChannelConnection | null; loading: boolean }) {
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: ['channel-catalog'], queryFn: getChannelCatalog, staleTime: 5 * 60_000 });
  const configured = catalog.data?.providers.find((p) => p.id === 'channex')?.configured === true;
  const [warnings, setWarnings] = useState<ChannexProvisionWarning[] | null>(null);
  const published = !!connection?.externalPropertyId;

  const provision = useMutation({
    mutationFn: () => provisionChannex(hotelId),
    onSuccess: (r) => {
      setWarnings(r.warnings);
      toast(r.warnings.length ? 'info' : 'success', r.propertyCreated ? 'Отель опубликован в Channex' : 'Отель опубликован заново', provisionSummary(r));
      qc.invalidateQueries({ queryKey: ['hotel', hotelId, 'connections'] });
      qc.invalidateQueries({ queryKey: ['admin', 'hotels'] });
    },
    onError: (err) => toast('error', 'Публикация не удалась', describeError(err)),
  });

  const run = () => {
    if (!published && !confirm('Создать объект этого отеля в боевом Channex? Объект платный — он попадёт в расходы Channex с этого месяца.')) return;
    provision.mutate();
  };

  return (
    <Card>
      <CardHeader
        icon={<Upload size={18} />}
        title="Публикация в Channex"
        subtitle="Создаёт объект, переносит каждую активную категорию с ценой как тип номера и тариф, открывает подключение и записывает соответствия. В кабинете отеля этой кнопки нет."
        action={(
          <Button size="sm" variant={published ? 'outline' : 'primary'} icon={<Upload size={13} />} loading={provision.isPending} disabled={loading || catalog.isPending || !configured} onClick={run}>
            {published ? 'Опубликовать заново' : 'Опубликовать в Channex'}
          </Button>
        )}
      />
      {catalog.isError ? <QueryError error={catalog.error} onRetry={() => catalog.refetch()} />
        : !catalog.isPending && !configured ? <Notice tone="warn" title="Channex не настроен на сервере">В окружении бэкенда нет ключа Channex — публиковать некуда.</Notice>
        : published ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
            <dt className="text-muted">Объект Channex</dt><dd className="text-right"><IdChip id={connection!.externalPropertyId!} /></dd>
            <dt className="text-muted">Подключение</dt><dd className="text-right">{(() => { const s = CONN_STATUS[connection!.status]; return <Badge tone={s?.tone ?? 'neutral'} dot>{s?.label ?? connection!.status}</Badge>; })()}</dd>
          </dl>
        ) : !loading && <p className="text-xs text-muted">Отель ещё не опубликован. Новые категории и канальные тарифы после первой публикации бэкенд доносит сам; эта кнопка — первая публикация и ремонт.</p>}
      {warnings && warnings.length > 0 && (
        <Notice tone="warn" icon={<ShieldAlert size={16} />} title="Площадки откажут без этого" className="mt-3">
          <ul className="list-disc space-y-1 pl-4">{warnings.map((w) => <li key={`${w.code}:${JSON.stringify(w.params ?? {})}`}>{channexWarningText(w)}</li>)}</ul>
        </Notice>
      )}
      {warnings && warnings.length === 0 && <Notice tone="ok" className="mt-3">Публикация прошла без замечаний.</Notice>}
    </Card>
  );
}

// ── Тарифы ────────────────────────────────────────────────────────────────────

function RatesTab({ hotelId }: { hotelId: string }) {
  const rates = useQuery({ queryKey: ['hotel', hotelId, 'rate-plans'], queryFn: () => listRatePlans(hotelId), retry: false });
  const categories = useQuery({ queryKey: ['hotel', hotelId, 'categories'], queryFn: () => listRoomCategories(hotelId), retry: false });
  const [q, setQ] = useUrlParam('pq');
  const [type, setType] = useUrlParam('ptype', 'all');
  const [active, setActive] = useUrlParam('pactive', 'all');
  const query = useDebounced(q.trim().toLowerCase(), 150);
  const catName = useMemo(() => new Map((categories.data ?? []).map((c) => [c.id, c.name])), [categories.data]);

  const filtered = useMemo(() => {
    let rows = rates.data ?? [];
    if (query) rows = rows.filter((r) => `${r.name} ${r.description ?? ''}`.toLowerCase().includes(query));
    if (type !== 'all') rows = rows.filter((r) => r.type === type);
    if (active !== 'all') rows = rows.filter((r) => r.isActive === (active === 'yes'));
    return rows;
  }, [rates.data, query, type, active]);

  if (rates.isError) return <AccessOrError error={rates.error} what="Тарифы отеля" route="/hotels/{id}/rate-plans" onRetry={() => rates.refetch()} />;

  const columns: Column<RatePlan>[] = [
    { key: 'name', header: 'Тариф', sortValue: (r) => r.name, render: (r) => <><div className="font-semibold text-ink">{r.name}</div>{r.description && <div className="line-clamp-1 text-xs text-muted">{r.description}</div>}</> },
    { key: 'type', header: 'Тип', sortValue: (r) => r.type, render: (r) => <Badge>{RATE_TYPE[r.type] ?? r.type}</Badge> },
    { key: 'price', header: 'За ночь', align: 'right', sortValue: (r) => r.pricePerNight, render: (r) => formatMoney(r.pricePerNight, r.currency) },
    { key: 'nights', header: 'Ночей', align: 'right', sortValue: (r) => r.minNights, hideBelow: 'md', render: (r) => `${r.minNights}${r.maxNights ? `–${r.maxNights}` : '+'}` },
    { key: 'dates', header: 'Период', sortValue: (r) => r.startDate, hideBelow: 'lg', className: 'text-xs text-muted whitespace-nowrap', render: (r) => r.startDate || r.endDate ? `${r.startDate ? formatDate(r.startDate) : '…'} – ${r.endDate ? formatDate(r.endDate) : '…'}` : 'всегда' },
    { key: 'cats', header: 'Категории', hideBelow: 'lg', render: (r) => r.categoryIds && r.categoryIds.length ? <span className="text-xs text-muted">{r.categoryIds.map((id) => catName.get(id) ?? id.slice(0, 8)).join(', ')}</span> : <span className="text-xs text-muted">все</span> },
    { key: 'flags', header: 'Условия', hideBelow: 'md', render: (r) => <span className="flex flex-wrap gap-1">{r.nonRefundable && <Badge tone="warn">невозвратный</Badge>}{r.mealPlan && <Badge>{r.mealPlan}</Badge>}{r.parentId && <Badge tone="accent">производный</Badge>}</span> },
    { key: 'priority', header: 'Приоритет', align: 'right', sortValue: (r) => r.priority, hideBelow: 'lg', render: (r) => r.priority },
    { key: 'isActive', header: 'Статус', align: 'center', sortValue: (r) => r.isActive, render: (r) => <Badge tone={r.isActive ? 'ok' : 'neutral'} dot>{r.isActive ? 'Активен' : 'Выключен'}</Badge> },
  ];

  return (
    <>
      <TableToolbar search={{ value: q, onChange: setQ, placeholder: 'Название тарифа' }} shown={filtered.length} total={rates.data?.length} onReset={q || type !== 'all' || active !== 'all' ? () => { setQ(''); setType('all'); setActive('all'); } : null}>
        <FilterSelect ariaLabel="Тип" value={type} onChange={setType} options={[{ value: 'all', label: 'Все типы' }, ...Object.entries(RATE_TYPE).map(([v, l]) => ({ value: v, label: l }))]} />
        <FilterSelect ariaLabel="Статус" value={active} onChange={setActive} options={[{ value: 'all', label: 'Все статусы' }, { value: 'yes', label: 'Активные' }, { value: 'no', label: 'Выключенные' }]} />
      </TableToolbar>
      <DataTable id="p_" rows={filtered} columns={columns} rowKey={(r) => r.id} defaultSort={{ key: 'priority', dir: 'desc' }} loading={rates.isPending} empty={{ icon: <Tag size={28} />, title: rates.data?.length ? 'Ничего не подошло под фильтры' : 'Тарифов нет' }} />
    </>
  );
}

// ── Демо-данные ───────────────────────────────────────────────────────────────

function DemoTab({ hotelId }: { hotelId: string }) {
  const qc = useQueryClient();
  const demo = useQuery({ queryKey: ['hotel', hotelId, 'demo'], queryFn: () => getDemoData(hotelId), retry: false });
  const invalidate = () => { qc.invalidateQueries({ queryKey: ['hotel', hotelId] }); qc.invalidateQueries({ queryKey: ['admin', 'hotels'] }); };
  const seed = useMutation({
    mutationFn: () => seedDemoData(hotelId),
    onSuccess: (r) => { toast('success', 'Демо-данные созданы', Object.entries(r).map(([k, v]) => `${k}: ${String(v)}`).join(', ')); invalidate(); },
    onError: (err) => toast('error', 'Не удалось создать', describeError(err)),
  });
  const clear = useMutation({
    mutationFn: () => clearDemoData(hotelId),
    onSuccess: (r) => { toast('success', 'Демо-данные удалены', Object.entries(r).map(([k, v]) => `${k}: ${String(v)}`).join(', ')); invalidate(); },
    onError: (err) => toast('error', 'Не удалось удалить', describeError(err)),
  });

  if (demo.isPending) return <Skeleton className="h-40" />;
  if (demo.isError) return <AccessOrError error={demo.error} what="Демо-данные" route="/hotels/{id}/demo-data" onRetry={() => demo.refetch()} />;

  return (
    <Card className="max-w-2xl">
      <CardHeader icon={<Database size={18} />} title="Демо-данные" subtitle="Сгенерированные номера, брони и блокировка — чтобы показать отелю заполненную систему. Помечены маркером, ручные данные не трогаются." />
      <Notice tone={demo.data.exists ? 'accent' : 'neutral'} title={demo.data.exists ? 'В отеле есть демо-данные' : 'Демо-данных нет'} className="mb-4">
        {demo.data.exists ? 'Повторная генерация вернёт существующие счётчики, а не удвоит данные — чтобы пересоздать, сначала удалите.' : 'Генерация добавит номера и брони; лимит номеров по тарифу организации учитывается (402 при превышении).'}
      </Notice>
      <div className="flex justify-end gap-2">
        {demo.data.exists
          ? <Button variant="danger" icon={<Trash2 size={14} />} loading={clear.isPending} onClick={() => { if (confirm('Удалить все демо-данные этого отеля?')) clear.mutate(); }}>Удалить демо-данные</Button>
          : <Button icon={<Wand2 size={14} />} loading={seed.isPending} onClick={() => seed.mutate()}>Сгенерировать</Button>}
      </div>
    </Card>
  );
}
